/** Manages registered SkillEffect instances and routes activation/deactivation to the correct skill each frame. */
import Phaser from 'phaser';
import { Component } from './Component';
import type { SkillEffect } from '../skills/SkillEffect';
import type { StaminaComponent } from './StaminaComponent';
import type { SkillProgressionComponent } from './SkillProgressionComponent';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugLog } from '../debug/DebugLog';

/** Holds a registry of active SkillEffects and dispatches player input to the correct toggle or instant activation. */
export class SkillActivationComponent implements Component {
  private effects: Map<string, SkillEffect> = new Map();
  /** Tracks each skill's isActive() state from the previous frame to detect transitions. */
  private previouslyActive: Map<string, boolean> = new Map();
  private events: Phaser.Events.EventEmitter;
  private stamina: StaminaComponent;
  private skillProgression: SkillProgressionComponent | null = null;

  constructor(events: Phaser.Events.EventEmitter, stamina: StaminaComponent) {
    this.events = events;
    this.stamina = stamina;
  }

  /** Set the SkillProgressionComponent so activations can record use-based XP. */
  setSkillProgression(progression: SkillProgressionComponent): void {
    this.skillProgression = progression;
  }

  /** Add a SkillEffect to the registry so it receives update ticks and can be dispatched. */
  registerEffect(effect: SkillEffect): void {
    this.effects.set(effect.skillId, effect);
  }

  /** Deactivate and destroy a SkillEffect, removing it from the registry. */
  unregisterEffect(skillId: string): void {
    const effect = this.effects.get(skillId);
    if (effect) {
      if (effect.isActive?.()) {
        effect.deactivate?.();
        this.events.emit('skill-deactivated', { skillId });
        DebugLog.log('skills', `${skillId}: deactivated (unregister)`);
      }
      effect.destroy();
      this.effects.delete(skillId);
      this.previouslyActive.delete(skillId);
    }
  }

  /** Toggle-style activation: if active, deactivate; if inactive, try to activate. */
  tryToggle(skillId: string): void {
    const effect = this.effects.get(skillId);
    if (!effect || effect.type !== 'active') return;

    if (effect.isActive?.()) {
      effect.deactivate?.();
      this.previouslyActive.set(skillId, false);
      this.events.emit('skill-deactivated', { skillId });
      DebugLog.log('skills', `${skillId}: deactivated`);
    } else {
      if (effect.canActivate && !effect.canActivate()) {
        const reason = effect.getBlockReason?.() ?? 'not ready';
        this.events.emit('skill-blocked', { skillId, reason });
        DebugLog.log('skills', `${skillId}: blocked (${reason})`);
        return;
      }
      const success = effect.activate?.() ?? false;
      if (success) {
        const cost = effect.staminaCost ?? ConfigLoader.combat.skillStamina.defaultCost;
        this.stamina.spend(cost);
        // Generic activation XP — fires for ALL skills including those with custom XP paths.
        // Melee skills also earn per-hit XP via CombatHitResolver; concealment earns sustained
        // XP via onXpGained; teleport earns danger-weighted XP via xpSource. Generic XP layers
        // underneath as a baseline activation reward. See P22-02 coexistence-decision.md.
        this.skillProgression?.recordUse(skillId);
        this.events.emit('skill-activated', { skillId });
        DebugLog.log('skills', `${skillId}: activated`);
      } else {
        this.events.emit('skill-failed', { skillId });
        DebugLog.log('skills', `${skillId}: activation failed`);
      }
    }
  }

  /** Fire-and-forget activation: call activate() without toggle-off behavior. */
  tryActivate(skillId: string): void {
    const effect = this.effects.get(skillId);
    if (!effect || effect.type !== 'active') return;

    if (effect.canActivate && !effect.canActivate()) {
      const reason = effect.getBlockReason?.() ?? 'not ready';
      this.events.emit('skill-blocked', { skillId, reason });
      DebugLog.log('skills', `${skillId}: blocked (${reason})`);
      return;
    }
    const success = effect.activate?.() ?? false;
    if (success) {
      const cost = effect.staminaCost ?? ConfigLoader.combat.skillStamina.defaultCost;
      this.stamina.spend(cost);
      // Generic activation XP — see comment in tryToggle() for coexistence rationale.
      this.skillProgression?.recordUse(skillId);
      this.events.emit('skill-activated', { skillId });
      DebugLog.log('skills', `${skillId}: activated`);
    } else {
      this.events.emit('skill-failed', { skillId });
      DebugLog.log('skills', `${skillId}: activation failed`);
    }
  }

  /** Route to tryToggle or tryActivate based on the skill's activationMode. */
  dispatch(skillId: string): void {
    const effect = this.effects.get(skillId);
    if (!effect) return;

    if (effect.activationMode === 'instant') {
      this.tryActivate(skillId);
    } else {
      this.tryToggle(skillId);
    }
  }

  /** Return true if the named skill's effect is currently active. */
  isSkillActive(skillId: string): boolean {
    return this.effects.get(skillId)?.isActive?.() ?? false;
  }

  /** Deactivate all currently-active effects (toggles, channels). Used by SimulationBridge to reset between scenarios. */
  deactivateAll(): void {
    for (const effect of this.effects.values()) {
      if (effect.isActive?.()) {
        effect.deactivate?.();
        this.previouslyActive.set(effect.skillId, false);
        this.events.emit('skill-deactivated', { skillId: effect.skillId });
      }
    }
  }

  /** Return true if any registered effect is currently in an active phase. */
  hasActiveEffects(): boolean {
    for (const effect of this.effects.values()) {
      if (effect.isActive?.()) return true;
    }
    return false;
  }

  /** Retrieve the typed SkillEffect instance for direct inspection (e.g., reading cooldown state). */
  getEffect<T extends SkillEffect>(skillId: string): T | undefined {
    return this.effects.get(skillId) as T | undefined;
  }

  update(delta: number): void {
    for (const effect of this.effects.values()) {
      const wasActive = this.previouslyActive.get(effect.skillId) ?? false;
      effect.update(delta);
      const nowActive = effect.isActive?.() ?? false;
      if (wasActive && !nowActive) {
        this.events.emit('skill-deactivated', { skillId: effect.skillId });
        DebugLog.log('skills', `${effect.skillId}: deactivated (auto)`);
      }
      this.previouslyActive.set(effect.skillId, nowActive);
    }
  }

  destroy(): void {
    this.deactivateAll();
    for (const effect of this.effects.values()) {
      effect.destroy();
    }
    this.effects.clear();
    this.previouslyActive.clear();
  }
}
